import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { Gtag } from 'angular-gtag';
import { MessageService } from 'primeng/api';
import { Observable, take, withLatestFrom } from 'rxjs';
import { DeductibleOverridesFormValues, PatientCopay } from 'src/app/core/models';
import {
  SetDeductibleOverridesUserData,
  UpdateCopayWithDeductibleOverrides,
} from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.actions';
import { OrderWizardDataState } from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.state';
import { ApiStoreData } from 'src/app/core/store/utils';
import { asFormControl } from 'src/app/core/utils/common';
import { validateAllFormFields } from 'src/app/core/utils/validators';

@Component({
  selector: 'app-deductible-overrides-form',
  templateUrl: './deductible-overrides-form.component.html',
  styleUrls: ['./deductible-overrides-form.component.scss'],
})
export class DeductibleOverridesFormComponent implements OnInit {
  @Input() confirmDeductibleOverrides: boolean = false;
  @Output() confirmDeductibleOverridesChange = new EventEmitter<boolean>();

  @Select(OrderWizardDataState.copay) copay$!: Observable<PatientCopay | null>;
  @Select(OrderWizardDataState.allowDeductibleOverrides) allowDeductibleOverrides$!: Observable<boolean>;
  @Select(OrderWizardDataState.hasUnsubmittedDeductibleOverrides)
  hasUnsubmittedDeductibleOverrides$!: Observable<boolean>;
  @Select(OrderWizardDataState.appliedDeductibleOverridesUserData)
  appliedDeductibleOverridesUserData$!: Observable<DeductibleOverridesFormValues | null>;
  @Select(OrderWizardDataState.deductibleOverridesUserData)
  deductibleOverridesUserData$!: Observable<DeductibleOverridesFormValues | null>;
  @Select(OrderWizardDataState.updateCopayDeductibleOverridesState)
  updateCopayDeductibleOverridesState$!: Observable<ApiStoreData<null>>;

  deductibleOverridesForm = new FormGroup({
    overridePrimaryDeductible: new FormControl(false),
    overrideSecondaryDeductible: new FormControl(false),
  });

  updateCopayAfterValueChanges: boolean = false;

  asFormControl = asFormControl;

  constructor(private store: Store, private messageService: MessageService, private gtag: Gtag) {}

  ngOnInit(): void {
    this.deductibleOverridesUserData$.pipe(take(1)).subscribe((userData: DeductibleOverridesFormValues | null) => {
      this.deductibleOverridesForm.setValue({
        overridePrimaryDeductible: userData?.overridePrimaryDeductible || false,
        overrideSecondaryDeductible: userData?.overrideSecondaryDeductible || false,
      });
    });

    this.updateCopayDeductibleOverridesState$.subscribe((updateState) => {
      if (updateState.isLoading) {
        this.deductibleOverridesForm.get('overridePrimaryDeductible')?.disable({ emitEvent: false });
        this.deductibleOverridesForm.get('overrideSecondaryDeductible')?.disable({ emitEvent: false });
      } else {
        this.deductibleOverridesForm.get('overridePrimaryDeductible')?.enable({ emitEvent: false });
        this.deductibleOverridesForm.get('overrideSecondaryDeductible')?.enable({ emitEvent: false });
      }
    });

    this.deductibleOverridesForm.valueChanges.subscribe((value) => {
      this.store
        .dispatch(
          new SetDeductibleOverridesUserData({
            overridePrimaryDeductible: value.overridePrimaryDeductible,
            overrideSecondaryDeductible: value.overrideSecondaryDeductible,
          })
        )
        .subscribe(() => {
          if (this.updateCopayAfterValueChanges) {
            this.updateCopayAfterValueChanges = false;
            this.updateCopay();
          }
        });
    });
  }

  updateCopay() {
    this.store
      .dispatch(new UpdateCopayWithDeductibleOverrides())
      .pipe(withLatestFrom(this.updateCopayDeductibleOverridesState$, this.appliedDeductibleOverridesUserData$))
      .subscribe(([_, updateCopayDeductibleOverridesState, appliedDeductibleOverridesUserData]) => {
        if (updateCopayDeductibleOverridesState.error) {
          this.deductibleOverridesForm.setValue({
            overridePrimaryDeductible: appliedDeductibleOverridesUserData?.overridePrimaryDeductible || false,
            overrideSecondaryDeductible: appliedDeductibleOverridesUserData?.overrideSecondaryDeductible || false,
          });

          this.messageService.add({
            key: 'homeToast',
            severity: 'error',
            detail: 'Could not update your estimated responsibility at this time. Please try again later.',
          });
        }
      });
  }

  onSubmit() {
    if (!this.deductibleOverridesForm.valid) {
      validateAllFormFields(this.deductibleOverridesForm);
      return;
    }

    this.updateCopay();
  }

  onConfirmDeductibleOverrides() {
    this.updateCopay();
    this.confirmDeductibleOverrides = false;
    this.confirmDeductibleOverridesChange.emit(this.confirmDeductibleOverrides);
    this.gtag.event('order_wizard_confirm_deductible_met_changes', {
      event_category: 'engagement',
    });
  }

  onResetDeductibleOverrides() {
    this.updateCopayAfterValueChanges = true;

    this.deductibleOverridesForm.setValue({
      overridePrimaryDeductible: false,
      overrideSecondaryDeductible: false,
    });

    this.confirmDeductibleOverrides = false;
    this.confirmDeductibleOverridesChange.emit(this.confirmDeductibleOverrides);
    this.gtag.event('order_wizard_revert_deductible_met_changes', {
      event_category: 'engagement',
    });
  }
}
